import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Check, Loader2, Smartphone } from "lucide-react";
import { api, errorMessage } from "../api";
import { fmtDateTime } from "./KotHistory";

export const ROLE_INFO = {
  manager: { label: "Manager", desc: "Everything except subscription, branch settings and staff" },
  cashier: { label: "Cashier", desc: "Tables, POS, customer bills, KOT history; can modify/cancel KOTs" },
  waiter: { label: "Waiter", desc: "Tables & KOT, final billing; can add notes or increase qty only" },
  kitchen: { label: "Kitchen", desc: "Kitchen Display only" },
};

function StaffForm({ initial, branches, onClose, onSaved }) {
  const [f, setF] = useState({ name: initial?.name || "", phone: (initial?.phone || "").replace("+91", ""), role: initial?.role || "waiter", branch_id: initial?.branch_id || branches[0]?.id || "", active: initial?.active ?? true });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const upd = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { initial ? await api.put(`/staff/${initial.id}`, f) : await api.post("/staff", f); onSaved(); }
    catch (e2) { setErr(errorMessage(e2)); } finally { setBusy(false); }
  };
  return (
    <div className="modal-backdrop"><div className="modal" data-testid="staff-modal">
      <div className="modal-head"><div><span className="overline">STAFF LOGIN</span><h2>{initial ? "Edit staff" : "Add staff"}</h2></div><button className="icon-button" onClick={onClose} data-testid="staff-modal-close"><X size={18} /></button></div>
      <form onSubmit={save}>
        <div className="form-grid">
          <label>Full name<input required value={f.name} onChange={(e) => upd("name", e.target.value)} data-testid="staff-name-input" /></label>
          <label>Mobile (10 digits)<input required inputMode="numeric" value={f.phone} onChange={(e) => upd("phone", e.target.value.replace(/[^\d+ ]/g, ""))} placeholder="Enter 10-digit mobile number" data-testid="staff-phone-input" /></label>
          <label>Role<select value={f.role} onChange={(e) => upd("role", e.target.value)} data-testid="staff-role-select">
            {Object.entries(ROLE_INFO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select></label>
          <label>Branch<select value={f.branch_id} onChange={(e) => upd("branch_id", e.target.value)} data-testid="staff-branch-select">
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select></label>
        </div>
        <p className="form-hint">{ROLE_INFO[f.role].desc}</p>
        <label style={{ display: "flex", gap: 8, alignItems: "center" }}><input type="checkbox" style={{ width: "auto", margin: 0 }} checked={f.active} onChange={(e) => upd("active", e.target.checked)} data-testid="staff-active-checkbox" /> Login enabled</label>
        {err && <div className="auth-alert error" data-testid="staff-form-error">{err}</div>}
        <button className="primary-button full" disabled={busy} data-testid="staff-save-button">{busy ? <Loader2 className="spin" size={14} /> : <Check size={14} />} Save</button>
      </form>
    </div></div>
  );
}

export default function StaffView({ branches }) {
  const [staff, setStaff] = useState([]);
  const [modal, setModal] = useState(null);
  const [err, setErr] = useState("");
  const load = async () => { try { setStaff((await api.get("/staff")).data); setErr(""); } catch (e) { setErr(errorMessage(e)); } };
  useEffect(() => { load(); }, []);
  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}'s login?`)) return;
    try { await api.delete(`/staff/${s.id}`); load(); } catch (e) { setErr(errorMessage(e)); }
  };
  const bname = (id) => branches.find((b) => b.id === id)?.name || "—";
  return (
    <>
      <div className="recipes-head">
        <div><span className="overline">TEAM ACCESS</span><h2 style={{ fontSize: 20, margin: "6px 0 2px" }}>Staff &amp; roles</h2>
          <p style={{ margin: 0, color: "var(--muted)", fontSize: 13 }}><Smartphone size={12} /> Staff sign in with their mobile number + OTP and only see their role's screens.</p></div>
        <button className="primary-button" onClick={() => setModal({ initial: null })} data-testid="add-staff-button"><Plus size={16} /> Add staff</button>
      </div>
      {err && <div className="auth-alert error" data-testid="staff-error">{err}</div>}
      <div className="panel full-panel"><div className="table-wrap"><table data-testid="staff-table">
        <thead><tr><th>Name</th><th>Mobile</th><th>Role</th><th>Branch</th><th>Status</th><th>Last login</th><th /></tr></thead>
        <tbody>
          {!staff.length && <tr><td colSpan={7}><div className="empty small">No staff yet. Add managers, cashiers, waiters and kitchen crew.</div></td></tr>}
          {staff.map((s) => (
            <tr key={s.id} data-testid={`staff-row-${s.id}`}>
              <td><b>{s.name}</b></td><td>{s.phone}</td>
              <td><span className="pill neutral" data-testid={`staff-role-${s.id}`}>{ROLE_INFO[s.role]?.label}</span></td>
              <td>{bname(s.branch_id)}</td>
              <td><span className={s.active ? "status success" : "status danger"}>{s.active ? "Active" : "Disabled"}</span></td>
              <td>{s.last_login_at ? fmtDateTime(s.last_login_at) : "—"}</td>
              <td style={{ display: "flex", gap: 4 }}>
                <button className="icon-button" onClick={() => setModal({ initial: s })} data-testid={`edit-staff-${s.id}`}><Pencil size={13} /></button>
                <button className="icon-button" onClick={() => remove(s)} data-testid={`delete-staff-${s.id}`}><Trash2 size={13} /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table></div></div>
      {modal && <StaffForm initial={modal.initial} branches={branches} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </>
  );
}
